import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router, RouterModule } from '@angular/router';
import { filter, map } from 'rxjs';
import { TranslatePipe } from '@ngx-translate/core';
import { InputTextModule } from 'primeng/inputtext';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { TreeModule } from 'primeng/tree';
import { TreeNode } from 'primeng/api';
import { TreeNodeSelectEvent } from 'primeng/types/tree';
import { SkeletonModule } from 'primeng/skeleton';
import { FormsModule } from '@angular/forms';
import { StageTreeNode } from '@cosmos/shared';
import { ScrollFadeDirective } from '@cosmos/angular/utils';
import { MonitoringTreeService } from '../monitoring-tree.service';

export type MonitoringTreeActiveContext =
  | { kind: 'dashboard' }
  | { kind: 'stage'; id: string }
  | { kind: 'measurement'; id: string };

@Component({
  selector: 'cosmos-monitoring-tree-rail',
  imports: [
    RouterModule, TranslatePipe, FormsModule, ScrollFadeDirective,
    InputTextModule, IconFieldModule, InputIconModule, TreeModule, SkeletonModule,
  ],
  templateUrl: './monitoring-tree-rail.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'block h-full' },
})
export class MonitoringTreeRailComponent {
  protected readonly treeService = inject(MonitoringTreeService);
  private readonly router = inject(Router);

  protected readonly filterText = signal('');

  protected readonly treeNodes = computed(() => {
    const filter = this.filterText().toLowerCase().trim();
    return this.treeService.roots()
      .map((root) => this.buildTreeNodes(root, filter))
      .filter((n): n is TreeNode => n !== null);
  });

  private readonly currentUrl = toSignal(
    this.router.events.pipe(
      filter((e): e is NavigationEnd => e instanceof NavigationEnd),
      map((e) => e.urlAfterRedirects),
    ),
    { initialValue: this.router.url },
  );

  protected readonly activeContext = computed<MonitoringTreeActiveContext>(() =>
    MonitoringTreeRailComponent.parseUrl(this.currentUrl()),
  );

  /** Pure URL parser, static so it's testable without a router or DI. Only
   *  'stage/:id' and 'measurement/:id' carry tree relevance — every other
   *  monitoring sub-route (the dashboard index, devices, data-explorer)
   *  parses to 'dashboard', meaning "nothing to highlight in the tree". */
  static parseUrl(url: string): MonitoringTreeActiveContext {
    const withoutQuery = url.split('?')[0];
    const stageMatch = withoutQuery.match(/\/monitoring\/stage\/([^/]+)/);
    if (stageMatch) return { kind: 'stage', id: stageMatch[1] };
    const measurementMatch = withoutQuery.match(/\/monitoring\/measurement\/([^/]+)/);
    if (measurementMatch) return { kind: 'measurement', id: measurementMatch[1] };
    return { kind: 'dashboard' };
  }

  /** Ancestor stage ids to auto-expand in the tree for the current
   *  activeContext — empty when the context is 'dashboard' or the target
   *  isn't in the currently-loaded forest yet. */
  protected readonly expandedStageIds = computed<Set<string>>(() => {
    const ctx = this.activeContext();
    if (ctx.kind === 'dashboard') return new Set();
    if (ctx.kind === 'stage') {
      return new Set(this.treeService.ancestorChain(ctx.id).map((s) => s._id));
    }
    const stageId = this.treeService.findMeasurementStageId(ctx.id);
    if (!stageId) return new Set();
    return new Set(this.treeService.ancestorChain(stageId).map((s) => s._id));
  });

  protected readonly activeMeasurementId = computed(() => {
    const ctx = this.activeContext();
    return ctx.kind === 'measurement' ? ctx.id : null;
  });

  protected readonly activeStageId = computed(() => {
    const ctx = this.activeContext();
    return ctx.kind === 'stage' ? ctx.id : null;
  });

  onNodeSelect(event: TreeNodeSelectEvent): void {
    const data = event.node.data;
    if (data?.type !== 'measurement' && data?.type !== 'stage') return;
    // Project id isn't tracked by this component directly — it's read off
    // the current URL's own /projects/:id/ segment, the same source of truth
    // the rest of the app uses for building monitoring links.
    const projectMatch = this.currentUrl().match(/\/projects\/([^/]+)\//);
    if (!projectMatch) return;
    const segment = data.type === 'measurement' ? 'measurement' : 'stage';
    this.router.navigate(['/projects', projectMatch[1], 'monitoring', segment, data.id]);
  }

  // Ported verbatim from the retired MeasurementsQuickSwitchComponent, plus
  // one additive change: `expanded` also opens ancestor stages of the active
  // route (expandedStageIds). Filter-driven expansion works exactly as before.
  // Converts a StageTreeNode into a PrimeNG TreeNode, recursively. Returns
  // null when a filter is active and nothing in this subtree matches — so
  // empty branches collapse out of the tree entirely.
  buildTreeNodes(stage: StageTreeNode, filter = ''): TreeNode | null {
    const childStageNodes = stage.children
      .map((c) => this.buildTreeNodes(c, filter))
      .filter((n): n is TreeNode => n !== null);

    const measurementNodes: TreeNode[] = stage.measurements
      .filter((m) => !filter || m.name.toLowerCase().includes(filter) || m.measurementType?.toLowerCase().includes(filter))
      .map((m) => ({
        label: m.name,
        data: { type: 'measurement', id: m._id, measurementType: m.measurementType },
        leaf: true,
        icon: 'fa-solid fa-chart-line',
      }));

    const allChildren = [...childStageNodes, ...measurementNodes];
    if (filter && allChildren.length === 0) return null;

    return {
      label: stage.name,
      data: { type: 'stage', id: stage._id },
      children: allChildren,
      icon: 'fa-solid fa-folder',
      // `expandedStageIds` is a `computed()` instance field — absent on a
      // bare Object.create(prototype) instance used in unit tests, so guard it.
      expanded: !!filter || (this.expandedStageIds?.() ?? new Set()).has(stage._id),
    };
  }
}
